import React from "react";
import { Stack } from "@origin-digital/ods-core";
import { CloseTransferStatusEnum } from "../../types";
import {
  ConnectionTypeEnum,
  ServiceTypeEnum,
} from "src/components/Premises/types";
import { serviceTypeToName } from "../helper";
import { apiClient } from "src/api/apiService";
import { useNavigate } from "react-router-dom";
import { CloseTransferData } from "../types";
import { normaliseDate } from "../../../../api/apiHelper";
import { CloseTransferFormLegacyHeaderSection } from "./CloseTransferFormLegacyHeaderSection";
import { CloseTransferFormLegacyServiceSection } from "./CloseTransferFormLegacyServiceSection";
import { useCloseTransferFormProps } from "./useCloseTransferFormProps";
import { CloseTransferFormLegacyActionSection } from "./CloseTransferFormLegacyActionSection";
import { CloseTransferFormSubmittedServiceSection } from "./CloseTransferFormSubmittedServiceSection";
import { useCloseTransferFormSetFieldValueEffect } from "./useCloseTransferFormSetFieldValueEffect";

const mapActionTypeToStatus = {
  cancel: CloseTransferStatusEnum.CANCELLED,
  action: CloseTransferStatusEnum.PROCESSED,
};

export const CloseTransferFormLegacy: React.FC = () => {
  const navigate = useNavigate();
  const {
    formikContext,
    selectedFormState,
    newPremiseFormContext,
    formBehavior,
  } = useCloseTransferFormProps();
  const { values } = formikContext;
  const { closeTransfer } = values;
  const { setDisplayingSnackbar, setFormState } = newPremiseFormContext;
  const { electricityService, gasServices, closeTransferPayload } =
    selectedFormState;
  useCloseTransferFormSetFieldValueEffect({
    formikContext,
    formBehavior,
  });

  const submitCloseTransfer = async () => {
    const _closeTransferPayload = closeTransfer.serviceTypes
      .map((serviceType) => ({
        service_type: serviceType,
        earliest_date:
          serviceType === ServiceTypeEnum.ELECTRICITY
            ? normaliseDate(closeTransfer.elecPreferDate, null)
            : normaliseDate(closeTransfer.gasPreferDate, null),
        chosen_action: closeTransfer.handoverType,
        close_transfer_status: CloseTransferStatusEnum.SUBMITTED,
        journey_id:
          serviceType === ServiceTypeEnum.ELECTRICITY
            ? electricityService?.journeyId
            : gasServices?.gasMeter?.journeyId || gasServices?.both?.journeyId,
      }))
      .filter((s) => !closeTransferPayload?.[s.service_type]?.id);

    const formData = new FormData();
    if (
      closeTransfer.signedTransferFormFiles &&
      closeTransfer.signedTransferFormFiles.length > 0
    ) {
      formData.append("file", closeTransfer.signedTransferFormFiles[0].file);
    }
    formData.append(
      "closeTransfers",
      new Blob([JSON.stringify(_closeTransferPayload)], {
        type: "application/json",
      })
    );

    console.log("_closeTransferPayload", _closeTransferPayload);

    try {
      const response = await apiClient.post("/close-transfer", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status) {
        navigate("/", {
          state: {
            submitted: true,
            journeyId:
              electricityService?.journeyId ||
              gasServices?.gasMeter?.journeyId ||
              gasServices?.both?.journeyId,
            snackbarMessage: "Close/transfer request lodged",
          },
        });
      }
    } catch (error) {
      setDisplayingSnackbar({
        message: "Failed to lodge close/transfer, please try again",
        tone: "caution",
      });
    }
  };

  const onUpdate = async (
    serviceType: ServiceTypeEnum,
    actionType: "cancel" | "action"
  ) => {
    try {
      const updatedCloseTransferPayload = closeTransferPayload![serviceType];

      const { data } = await apiClient.put<CloseTransferData[]>(
        `/close-transfer`,
        [
          {
            ...updatedCloseTransferPayload,
            close_transfer_status: mapActionTypeToStatus[actionType],
          },
        ]
      );

      if (actionType === "action") {
        setDisplayingSnackbar({
          message: `${serviceTypeToName[serviceType]} account closed`,
          tone: "positive",
        });
      }

      if (actionType === "cancel") {
        setDisplayingSnackbar({
          message: `${serviceTypeToName[serviceType]} account closure cancelled`,
          tone: "positive",
        });
      }

      const updatedCloseTransferData = data.find(
        (service) => service.service_type === serviceType
      );

      if (serviceType === ServiceTypeEnum.ELECTRICITY) {
        setFormState((prev) => ({
          ...prev,
          closeTransfer: {
            ...prev.closeTransfer,
            [serviceType]: updatedCloseTransferData,
          },
          electricityService: {
            ...prev.electricityService,
            requestStatus: updatedCloseTransferData?.request_status,
          },
        }));
      }

      if (serviceType === ServiceTypeEnum.GAS) {
        const gasServiceName: "gasMeter" | "both" =
          updatedCloseTransferData?.connection_type ===
          ConnectionTypeEnum.METER_FIX
            ? "gasMeter"
            : "both";
        setFormState((prev) => ({
          ...prev,
          gasServices: {
            ...prev.gasServices,
            [gasServiceName]: {
              ...prev.gasServices![gasServiceName],
              requestStatus: updatedCloseTransferData?.request_status,
            },
          },
          closeTransfer: {
            ...prev.closeTransfer,
            [serviceType]: updatedCloseTransferData,
          },
        }));
      }
    } catch (error) {
      if (actionType === "action") {
        setDisplayingSnackbar({
          message: "Failed to close account, please try again",
          tone: "caution",
        });
      }

      if (actionType === "cancel") {
        setDisplayingSnackbar({
          message: "Failed to cancel account closure, please try again",
          tone: "caution",
        });
      }
    }
  };

  return (
    <Stack
      space={{
        xs: "xxlarge",
        md: "xxxlarge",
      }}
    >
      {/* Form container */}
      <Stack
        space={{
          xs: "xlarge",
          md: "xxlarge",
        }}
      >
        {/* Header */}
        <CloseTransferFormLegacyHeaderSection />
        {/* End Header */}

        {/* Services */}
        <CloseTransferFormLegacyServiceSection onUpdate={onUpdate} />
        {/* End Services */}
      </Stack>
      {/* End Form container */}

      {/* Actions */}
      <CloseTransferFormLegacyActionSection
        submitCloseTransfer={submitCloseTransfer}
      />
      {/* End Actions */}

      {/* Submitted services */}
      <CloseTransferFormSubmittedServiceSection />
      {/* End Submitted services */}
    </Stack>
  );
};
